import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { usePublicPlans } from "@/api/queries/publicPlans";
import { Accordion, type AccordionItem } from "@/components/ui/Accordion";

import { PlatformBrandingNotice } from "./PlatformBrandingNotice";

const FAQ_KEYS = [
  "scope",
  "custom",
  "pricing",
  "card",
  "setup",
  "manual",
  "delivery",
  "noDxf",
  "ownership",
  // The invoicing / VAT detail used to be a feature card, where a shop owner
  // met "§29 ZDPH, OSS/IOSS, VIES, ARES" before anything they had asked
  // about. The card now says the invoice comes out the way their accountant
  // wants it; the paragraph the accountant needs lives here, where detail is
  // what the reader came for (KAN-1114).
  "paperwork",
  "changes",
] as const;

/**
 * Operator-facing FAQ on the SaaS marketing landing.
 *
 * Different audience from the customer-facing `<HomeFaq>` (which sells
 * the laser-cutting service to end customers). This one pitches the
 * Forgmatic SaaS to prospective shop owners, copy lives in
 * `common.json → platformLanding.faq.*`.
 *
 * UI is the same shared `<Accordion>` primitive used by the
 * per-process FAQ — keeps a single accordion implementation across the
 * site.
 */
export function PlatformFaq(): JSX.Element {
  const { t } = useTranslation("common");
  const plans = usePublicPlans();
  // ``faq.items.pricing.a`` quotes the free-plan monthly inquiry cap.
  // Source of truth is ``Plan(tier='free').max_inquiries_per_month``,
  // exposed via the ``max_inquiries_per_month`` metric row on
  // ``/public/plans``. ``null`` = "unlimited" → swap to ``aUnlimited``
  // template so we don't render "with a monthly cap of inquiries"
  // with an empty number. While the query is loading we also fall
  // back to the unlimited template (better than flashing 0).
  const freeInquiriesPerMonth = useMemo<number | null>(() => {
    const metric = plans.data?.metrics.find(
      (m) => m.key === "max_inquiries_per_month",
    );
    if (!metric) return null;
    const value = metric.values["free"];
    return typeof value === "number" ? value : null;
  }, [plans.data]);

  const items: AccordionItem[] = useMemo(
    () =>
      FAQ_KEYS.map((key) => {
        const answerKey =
          key === "pricing" && freeInquiriesPerMonth === null
            ? "platformLanding.faq.items.pricing.aUnlimited"
            : `platformLanding.faq.items.${key}.a`;
        return {
          id: key,
          title: t(`platformLanding.faq.items.${key}.q`),
          // The plan sentence joins the ownership answer instead of
          // hanging under it as a second, repeated note (KAN-1946).
          body: (
            <p>
              {t(answerKey, { freeInquiriesPerMonth })}
              {key === "ownership" ? (
                <>
                  {" "}
                  <PlatformBrandingNotice inline />
                </>
              ) : null}
            </p>
          ),
        };
      }),
    [t, freeInquiriesPerMonth],
  );

  return (
    // ``#cenik`` is where the design's "Ceník" nav lands: the pricing
    // question lives in this list.
    <section className="section alt center" id="cenik">
      <div className="inner tight">
        <p className="kicker">{t("platformLanding.faq.kicker")}</p>
        <h2 className="h2">{t("platformLanding.faq.heading")}</h2>

        {/* Same shared <Accordion>; the design's look is applied to it
            page-scoped (``.pl .faq`` in PlatformLanding.css). */}
        <Accordion
          items={items}
          marker="plus-minus"
          initialOpenIndex={0}
          className="faq"
          testId="platform-faq"
        />
      </div>
    </section>
  );
}
